<template>
  <div class="uploader">
    <div
      class="drop-zone"
      :class="{ active: dragging, filled: files.length }"
      @dragover.prevent="dragging = true"
      @dragleave="dragging = false"
      @drop.prevent="onDrop"
    >
      <template v-if="!files.length">
        <p class="title">Arrastra tu carpeta aquí</p>
        <label class="btn">
          Seleccionar carpeta
          <input type="file" webkitdirectory multiple hidden @change="onInput" />
        </label>
      </template>
      <template v-else>
        <p class="title">{{ folderName }}</p>
        <p class="subtitle">{{ files.length }} archivos · {{ totalSize }}</p>
      </template>
    </div>

    <div v-if="uploading || done" class="progress-block">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: globalProgress + '%' }" />
      </div>
      <div class="progress-stats">
        <span>{{ globalProgress }}%</span>
        <span>{{ uploadedCount }}/{{ files.length }} archivos</span>
        <span v-if="speed">{{ speed }}</span>
      </div>
    </div>

    <p v-if="currentFile" class="current-file">↑ {{ currentFile }}</p>

    <div v-if="paused"    class="alert alert--warn">Conexión interrumpida — reanudando automáticamente...</div>
    <div v-if="errorMsg"  class="alert alert--error">{{ errorMsg }}</div>
    <div v-if="done"      class="alert alert--ok">✓ Carpeta subida correctamente</div>

    <div class="actions">
      <button v-if="files.length && !uploading && !done" class="btn btn--primary" @click="startUpload">Subir carpeta</button>
      <button v-if="uploading"        class="btn btn--ghost"   @click="cancelUpload">Cancelar</button>
      <button v-if="done || errorMsg" class="btn btn--outline" @click="reset">Subir otra</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import config from '@/config.js'

// ── Constantes ────────────────────────────────────────────────────────────────
const CHUNK_SIZE   = 5 * 1024 * 1024
const API_BASE     = `${config.apiBaseUrl}/upload`   // ← usa config centralizado
const CONCURRENCY  = 2
const MAX_RETRIES  = 5
const RETRY_DELAYS = [1000, 3000, 6000, 12000, 24000]

// ── Estado ────────────────────────────────────────────────────────────────────
const files          = ref([])
const uploading      = ref(false)
const done           = ref(false)
const paused         = ref(false)
const cancelled      = ref(false)
const currentFile    = ref('')
const uploadedCount  = ref(0)
const globalProgress = ref(0)
const speed          = ref('')
const errorMsg       = ref('')
const dragging       = ref(false)

let startTime    = 0
let bytesUploaded = 0

// ── Computed ──────────────────────────────────────────────────────────────────
const folderName = computed(() => {
  const first = files.value[0]?.webkitRelativePath || ''
  return first.split('/')[0] || 'Carpeta'
})

const totalSize = computed(() => {
  const mb = files.value.reduce((s, f) => s + f.size, 0) / 1024 / 1024
  return mb > 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb.toFixed(0) + ' MB'
})

// ── Carga de archivos ─────────────────────────────────────────────────────────
function onInput(e) {
  files.value = [...e.target.files]
}

async function onDrop(e) {
  dragging.value = false
  const all = []
  for (const item of [...e.dataTransfer.items]) {
    const entry = item.webkitGetAsEntry?.()
    if (entry) all.push(...await traverse(entry))
  }
  files.value = all
}

async function traverse(entry, path = '') {
  if (entry.isFile) {
    const f = await new Promise(r => entry.file(r))
    f.relativePath = path + f.name
    return [f]
  }
  const entries = await new Promise(r => entry.createReader().readEntries(r))
  const nested  = await Promise.all(entries.map(en => traverse(en, path + entry.name + '/')))
  return nested.flat()
}

// ── Upload principal ──────────────────────────────────────────────────────────
async function startUpload() {
  uploading.value      = true
  done.value           = false
  cancelled.value      = false
  errorMsg.value       = ''
  uploadedCount.value  = 0
  globalProgress.value = 0
  startTime            = Date.now()
  bytesUploaded        = 0

  const queue = [...files.value]
  try {
    await Promise.all(
      Array(CONCURRENCY).fill(null).map(() => uploadWorker(queue))
    )
    if (!cancelled.value) {
      done.value        = true
      currentFile.value = ''
    }
  } catch (err) {
    errorMsg.value = 'Error al subir: ' + err.message
  } finally {
    uploading.value = false
  }
}

async function uploadWorker(queue) {
  while (queue.length && !cancelled.value) {
    const file = queue.shift()
    await uploadFile(file)
    uploadedCount.value++
    globalProgress.value = Math.round(uploadedCount.value / files.value.length * 100)
  }
}

// ── Upload de un archivo con reanudación ──────────────────────────────────────
async function uploadFile(file) {
  const relativePath = file.relativePath || file.webkitRelativePath
  const fileId       = getOrCreateFileId(file)
  const totalChunks  = Math.ceil(file.size / CHUNK_SIZE)
  currentFile.value  = relativePath

  const alreadyReceived = await fetchReceivedChunks(fileId)
  const pending = Array.from({ length: totalChunks }, (_, i) => i)
    .filter(i => !alreadyReceived.includes(i))

  for (const index of pending) {
    if (cancelled.value) return
    const start = index * CHUNK_SIZE
    const chunk = file.slice(start, start + CHUNK_SIZE)

    await uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath })

    bytesUploaded += chunk.size
    const elapsed = (Date.now() - startTime) / 1000
    speed.value   = (bytesUploaded / 1024 / 1024 / elapsed).toFixed(1) + ' MB/s'
  }

  localStorage.removeItem('upload_' + file.name + '_' + file.size)
}

// ── Chunk con reintentos automáticos ──────────────────────────────────────────
async function uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath }) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      paused.value = false
      const formData = new FormData()
      formData.append('file',         chunk)
      formData.append('fileId',       fileId)
      formData.append('chunkIndex',   index)
      formData.append('totalChunks',  totalChunks)
      formData.append('relativePath', relativePath)

      const res = await fetch(`${API_BASE}/chunk`, { method: 'POST', body: formData })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return
    } catch (err) {
      if (attempt === MAX_RETRIES) throw err
      paused.value = true
      await sleep(RETRY_DELAYS[attempt] ?? 24000)
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
async function fetchReceivedChunks(fileId) {
  try {
    const res  = await fetch(`${API_BASE}/chunk/status/${fileId}`)
    const data = await res.json()
    return data.receivedChunks || []
  } catch {
    return []
  }
}

function getOrCreateFileId(file) {
  const key = 'upload_' + file.name + '_' + file.size
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms))
}

function cancelUpload() {
  cancelled.value   = true
  uploading.value   = false
  currentFile.value = ''
}

function reset() {
  files.value          = []
  done.value           = false
  errorMsg.value       = ''
  globalProgress.value = 0
  speed.value          = ''
  cancelled.value      = false
  paused.value         = false
  currentFile.value    = ''
  uploadedCount.value  = 0
}
</script>

<style scoped>
.uploader {
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}
.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 40px 24px;
  text-align: center;
  transition: all 0.2s;
  background: #fafafa;
}
.drop-zone.active { border-color: #3b82f6; background: #eff6ff; }
.drop-zone.filled { border-style: solid; border-color: #10b981; background: #f0fdf4; }
.title    { font-size: 1rem; font-weight: 600; color: #111827; margin: 0 0 4px; }
.subtitle { font-size: 0.875rem; color: #6b7280; margin: 0; }
.progress-block { display: flex; flex-direction: column; gap: 6px; }
.progress-bar   { height: 8px; background: #e5e7eb; border-radius: 99px; overflow: hidden; }
.progress-fill  { height: 100%; background: #3b82f6; border-radius: 99px; transition: width 0.3s; }
.progress-stats { display: flex; justify-content: space-between; font-size: 0.8rem; color: #6b7280; }
.current-file   { font-size: 0.8rem; color: #6b7280; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.alert         { padding: 12px 14px; border-radius: 8px; font-size: 0.875rem; }
.alert--warn   { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.alert--ok     { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
.alert--error  { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
.actions { display: flex; gap: 10px; }
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.btn--primary { background: #3b82f6; color: #fff; flex: 1; }
.btn--primary:hover { background: #2563eb; }
.btn--outline { background: transparent; border: 1px solid #d1d5db; color: #374151; flex: 1; }
.btn--outline:hover { background: #f9fafb; }
.btn--ghost  { background: transparent; color: #6b7280; border: 1px solid #e5e7eb; }
.btn--ghost:hover { background: #f9fafb; }
</style>